/**
 * Shared Chart Components
 *
 * Reusable UI components for dashboard charts
 */

import { ArrowDownRight, ArrowUpRight } from 'lucide-react';

/**
 * KPI Card Component
 *
 * Displays a key performance indicator with icon, value, trend
 */
interface KpiCardProps {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  trend?: string;
  trendUp?: boolean;
  accent: string;
}

export function KpiCard({ label, value, sub, icon: Icon, trend, trendUp, accent }: KpiCardProps) {
  return (
    <div className="bg-card border border-border rounded-lg p-5 flex flex-col gap-3 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <div>
        <div className="text-3xl font-bold text-foreground tracking-tight">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </div>
      {trend && (
        <div
          className={`flex items-center gap-1 text-xs ${
            trendUp ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
          }`}
        >
          {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {trend}
        </div>
      )}
    </div>
  );
}

/**
 * Section Title Component
 *
 * Consistent heading style for chart sections
 */
export function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
      {children}
    </h2>
  );
}

/**
 * Custom Tooltip for Recharts
 *
 * Styled tooltip with proper value formatting
 */
export const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-card border border-border rounded-lg shadow-lg px-3 py-2 text-xs">
      {label && <p className="font-semibold text-foreground mb-1">{label}</p>}
      {payload &&
        payload.length > 0 &&
        payload.map((p: any, index: number) => {
          const uniqueKey = `tooltip-${label || 'nolabel'}-${p.dataKey || p.name || 'unknown'}-${index}-${p.value}`;
          return (
            <div key={uniqueKey} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
              <span className="text-muted-foreground">{p.name}:</span>
              <span className="text-foreground font-medium">
                {p.name?.toLowerCase().includes('value') || p.name?.toLowerCase().includes('£')
                  ? `£${Number(p.value).toLocaleString()}`
                  : p.value}
              </span>
            </div>
          );
        })}
    </div>
  );
};
